// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import update from 'immutability-helper';
import { generateTags } from '../../../elements/Filter/utils';
import Filter from '../../../elements/Filter/Filter';
import Loading from '../../../elements/Loading/Loading';
import FilterDrawer from '../../../elements/Filter/Drawer/FilterDrawer';
import ProductBox from '../../featured-products/components/ProductBox/ProductBox';
import InfiniteScroll from '../../../elements/InfiniteScroll/InfiniteScroll';
import classNames from 'classnames/bind';
import { Helmet } from 'react-helmet';
import Banner from '../../../elements/Banner/Banner';

import style from '../../featured-products/components/ProductsCommon.scss';
import icons from '../../../assets/styles/icons.scss';
import filterOptionsStyle from '../../../assets/styles/filter-options.scss';
import filterStyles from '../../../assets/styles/filter.scss';
import headerImage from '../../../assets/img/popular-products.png';

import type { HTTPError } from '../../../error';
import type { ProductsFilter } from '../duck';
import type { Product } from '../../featured-products/duck';
import type { FilterCheckbox } from '../../../elements/Filter/Filter';
import type { LocalValuesState } from '../../local-values/duck';
import type { LocaleState } from '../../locale/duck';
import type { Category, OnlineStore } from '../../../entities';

type Props = {
  +localValues: LocalValuesState;
  +localeState: LocaleState;
  +pageCanAsk: boolean;
  +page: number;
  +isFetchingProducts: boolean;
  +fetchProductsError: HTTPError;
  +products: Product[];
  +fetchPopularProducts: (filters: ProductsFilter, resetPagination?: boolean) => void;
  +resetAlertState: () => void;
  +resetState: () => void;
  +chosenVariants?: { [key: string]: number };
};

type State = {
  drawerOpen: boolean;
  filter: {
    categories: {
      [key: string]: boolean;
    };
    openCategories: boolean;
    stores: {
      [key: string]: boolean;
    };
    openStores: boolean;
    minPrice: {
      value: string;
    };
    maxPrice: {
      value: string;
    };
    openPrice: boolean;
    freeOptions: {
      [key: string]: boolean;
    };
    openFreeOptions: boolean;
  };
};

const messages = defineMessages({
  freeShipping: {
    id: 'featuredProducts.filter.freeShipping',
  },
  freeTaxes: {
    id: 'featuredProducts.filter.taxFree',
  },
  US: {
    title: {
      id: 'popularProducts.document.title.US',
    },
    description: {
      id: 'popularProducts.document.description.US',
    },
  },
  PE: {
    title: {
      id: 'popularProducts.document.title.PE',
    },
    description: {
      id: 'popularProducts.document.description.PE',
    },
  },
  CO: {
    title: {
      id: 'popularProducts.document.title.CO',
    },
    description: {
      id: 'popularProducts.document.description.CO',
    },
  },
});

const initialState = {
  drawerOpen: false,
  filter: {
    categories: {},
    openCategories: true,
    stores: {},
    openStores: true,
    minPrice: {
      value: '0'
    },
    maxPrice: {
      value: '5000'
    },
    openPrice: true,
    freeOptions: {},
    openFreeOptions: true,
  }
};

export default class PopularProducts extends Component<Props, State> {
  state = initialState;

  componentDidMount() {
    const { fetchPopularProducts, products } = this.props;
    if (!products) {
      fetchPopularProducts(this.buildQueryParams(), true);
    }
  }

  componentWillUnmount() {
    const { resetAlertState, resetState } = this.props;
    resetAlertState();
    resetState();
  }

  handleOpenDrawer = () => {
    this.setState({ drawerOpen: true });
  };

  handleCloseDrawer = () => {
    const { fetchPopularProducts } = this.props;
    this.setState({
      drawerOpen: false,
    });
    fetchPopularProducts(this.buildQueryParams(), true);
  };

  handleOpen = (name: string) => {
    this.setState((state: State) => {
      return {
        filter: update(state.filter, {
          [name]: { $set: !state.filter[name] },
        }),
      };
    });
  };

  handleChangeCategory(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('categories', { [value]: !filter.categories[value] });
    }
  }

  handleChangeStore(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('stores', { [value]: !filter.stores[value] });
    }
  }

  handleChangeFreeOptions(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('freeOptions', { [value]: !filter.freeOptions[value] });
    }
  }

  handleChangePrice = (values: number[], isDesktop: boolean = true) => {
    this.handleChangeFilterSingleValueCreator(isDesktop)('minPrice', { value: values[0] });
    this.handleChangeFilterSingleValueCreator(isDesktop)('maxPrice', { value: values[1] });
  };

  buildQueryParams(): { [key: string]: any } {
    const params = {};
    const { filter } = this.state;

    const categories = Object.keys(filter.categories)
      .reduce((acum, act: string) => filter.categories[act] ? acum.concat(act) : acum, []);
    const stores = Object.keys(filter.stores)
      .reduce((acum, act: string) => filter.stores[act] ? acum.concat(act) : acum, []);

    if (filter.minPrice) params.minPrice = parseInt(filter.minPrice.value, 10);
    if (filter.maxPrice) params.maxPrice = parseInt(filter.maxPrice.value, 10);
    if (categories.length) params.categories = categories.join(',');
    if (stores.length) params.stores = stores.join(',');
    Object.keys(filter.freeOptions).forEach((key: string) => {
      if (filter.freeOptions[key]) params[key] = 1;
    });
    return params;
  }

  handleClearFilters = () => {
    const { fetchPopularProducts } = this.props;
    this.setState(initialState,
      () => fetchPopularProducts(this.buildQueryParams(), true));
  };

  handleDeleteTag = (key?: string, name?: string) => {
    const { fetchPopularProducts } = this.props;
    const updateQuery = {};
    switch (key) {
      case 'categories':
      case 'stores':
      case 'freeOptions':
        if (name) {
          updateQuery[key] = {
            [name]: { $set: false },
          };
        }
        break;
    }
    this.setState((prevState: State) => update(prevState, {
      filter: updateQuery,
    }), () => fetchPopularProducts(this.buildQueryParams(), true));
  };

  handleChangeFilterValueCreator = (isDesktop: boolean) =>
    (name: string, value: any) => {
      const { fetchPopularProducts } = this.props;
      this.setState((state: State) => {
        const c = update(state.filter[name], { $merge: value });
        return {
          filter: update(state.filter, {
            [name]: { $set: c },
          }),
        };
      }, () => {
        if (isDesktop) {
          fetchPopularProducts(this.buildQueryParams(), true);
        }
      });
    };

  handleChangeFilterSingleValueCreator = (isDesktop: boolean) =>
    (name: string, value: any) => {
      const { fetchPopularProducts } = this.props;
      this.setState((state: State) => {
        //const c = update(state.filter[name], { $set: value });
        const c = value;
        return {
          filter: update(state.filter, {
            [name]: { $set: c },
          }),
        };
      }, () => {
        if (isDesktop) {
          fetchPopularProducts(this.buildQueryParams(), true);
        }
      });
    };

  generateFilterFields = (categories: ?FilterCheckbox[], stores: ?FilterCheckbox[], freeOptions: ?FilterCheckbox[], { filter }: State, isDesktop: boolean) => [
    // {
    //   type: 'checkbox',
    //   titleIntl: 'filter.categories.title',
    //   handler: (e: Event) => this.handleChangeCategory(e, 'categories', isDesktop),
    //   stateValue: filter.categories,
    //   name: 'categories',
    //   checkboxes: categories,
    //   isOpen: filter.openCategories,
    //   handleOpen: () => this.handleOpen('openCategories'),
    // },
    {
      type: 'checkbox',
      titleIntl: 'filter.stores.freeOptions',
      handler: (e: Event) => this.handleChangeFreeOptions(e, isDesktop),
      stateValue: filter.freeOptions,
      name: 'freeOptions',
      checkboxes: freeOptions,
      isOpen: filter.openFreeOptions,
      handleOpen: () => this.handleOpen('openFreeOptions'),
    },
    {
      type: 'range',
      titleIntl: 'filter.price.title',
      isOpen: filter.openPrice,
      name: 'price',
      minStateValue: filter.minPrice,
      maxStateValue: filter.maxPrice,
      options: {
        min: 0,
        max: 5000,
        defaultValue: [0, 5000],
        onChange: (values: number[]) => this.handleChangePrice(values, isDesktop),
      },
      handleOpen: () => this.handleOpen('openPrice'),
    },
    {
      type: 'checkbox',
      titleIntl: 'filter.stores.title',
      handler: (e: Event) => this.handleChangeStore(e, isDesktop),
      stateValue: filter.stores,
      name: 'stores',
      checkboxes: stores,
      isOpen: filter.openStores,
      handleOpen: () => this.handleOpen('openStores'),
    },
  ];

  render() {
    const { drawerOpen } = this.state;
    const {
      localValues, localeState, fetchPopularProducts,
      isFetchingProducts, pageCanAsk, products, chosenVariants,
    } = this.props;
    const categories = localValues.categories && localValues.categories.map((c: Category) => (
      {
        label: c.translations[localeState.locale.toUpperCase()].name,
        value: c.id.toString(),
      }
    ));
    const stores = localValues.onlineStores && localValues.onlineStores.map((s: OnlineStore) => (
      {
        label: s.name,
        value: s.id.toString(),
      }
    ));

    const freeOptions = [
      {
        name: 'freeShipping',
        translation: this.context.intl.formatMessage(messages.freeShipping),
      },
      {
        name: 'taxFree',
        translation: this.context.intl.formatMessage(messages.freeTaxes),
      }
    ].map((f: any) => (
      {
        label: f.translation,
        value: f.name,
      }
    ));

    const filterFields = this.generateFilterFields(categories, stores, freeOptions, this.state, true);
    const filterContent = <Filter
      filterFields={(this.generateFilterFields(categories, stores, freeOptions, this.state, true): any)}
      handleDeleteTag={this.handleDeleteTag}/>;
    const tags: any = generateTags((filterFields: any), this.handleDeleteTag, this.context.intl.formatMessage);
    const noResult = (!products || !products.length) && !isFetchingProducts;
    const popularProducts = products && products.map((p: Product, index: number) => (
      <ProductBox product={p} key={index}/>));

    const title = this.context.intl.formatMessage((messages[localeState.domainCountry] || messages.default).title);
    const description = this.context.intl.formatMessage(
      (messages[localeState.domainCountry] || messages.default).description);

    const banner = <Banner color={'blue'}
                           titleElement={<FormattedMessage id="popularProducts.header.title">{
                             txt => <h1><i className={icons.iconStarMedium}/> {txt}</h1>
                           }</FormattedMessage>}/>;
    return (
      <Fragment>
        <Helmet meta={[
          { name: 'description', content: description },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { name: 'twitter:title', content: title },
          { name: 'twitter:twitter:description', content: description },
        ]}>
          <title>{title}</title>
        </Helmet>
        {banner}
        <section className={filterOptionsStyle.filterOptions}>
          <section className={filterOptionsStyle.header}>
            <FormattedMessage id="stores.filter.title">{
              text => <p className={filterOptionsStyle.title}><i className={icons.symbolTweak}/>{text}</p>
            }</FormattedMessage>
            {/*<section className={filterOptionsStyle.sortOptions}>
              <FormattedMessage id="sort.title">{
                txt => <label>{txt}</label>
              }</FormattedMessage>
            </section>*/}
            <section className={filterOptionsStyle.filterDrawer}>
              <FilterDrawer drawerOpen={drawerOpen}
                            filterFields={(this.generateFilterFields(categories, stores, freeOptions,
                              this.state, false): any)}
                            color="lightBlue"
                            handleCloseDrawer={this.handleCloseDrawer}
                            handleOpenDrawer={this.handleOpenDrawer}
                            handleClearFilters={this.handleClearFilters}/>
            </section>
          </section>
        </section>
        <section key="mainWrap" className={classNames(style.mainWrap)}>
          <aside className={style.filterWrap}>
            {filterContent}
          </aside>
          <section className={style.rightWrap}>
            {!!tags.length && <ul className={filterStyles.tags}>
              {tags}
            </ul>}
            {noResult && <header>
              <FormattedMessage id="orders.pasts.noResults">{
                txt => <p>{txt}</p>
              }</FormattedMessage>
            </header>}
            <InfiniteScroll offset={650}
                             disabled={isFetchingProducts || !pageCanAsk}
                             handleCheckpoint={() => fetchPopularProducts(this.buildQueryParams())}>
              <section className={style.productWrap}>
                {popularProducts}
              </section>
            </InfiniteScroll>
            {isFetchingProducts && <Loading className={style.loading}/>}
          </section>
        </section>
      </Fragment>);
  }
}

PopularProducts.propTypes = {
  pageCanAsk: PropTypes.bool,
  page: PropTypes.number,
  isFetchingProducts: PropTypes.bool,
  fetchProductsError: PropTypes.object,
  products: PropTypes.array,
  fetchPopularProducts: PropTypes.func.isRequired,
  resetAlertState: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  localValues: PropTypes.object.isRequired,
  localeState: PropTypes.object.isRequired,
  chosenVariants: PropTypes.object,
};

PopularProducts.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/popular-products/components/PopularProducts.jsx